/**
 * Notification sound playback utility.
 *
 * Preloads a short chime and plays it on demand.
 * Respects browser autoplay policies by tracking whether the user
 * has interacted with the page at least once before attempting playback.
 */

const SOUND_URL = '/sounds/notification.mp3';

let audio: HTMLAudioElement | null = null;
let hasUserInteracted = false;

/**
 * Track user interaction so we know autoplay is allowed.
 * Each listener uses `{ once: true }` to self-remove after firing.
 */
function trackUserInteraction(): void {
  if (typeof window === 'undefined') return;

  const markInteracted = () => { hasUserInteracted = true; };

  window.addEventListener('click', markInteracted, { once: true });
  window.addEventListener('keydown', markInteracted, { once: true });
  window.addEventListener('touchstart', markInteracted, { once: true });
}

/**
 * Preload the notification audio element.
 * Safe to call multiple times — only creates one Audio instance.
 */
function ensureAudioLoaded(): HTMLAudioElement | null {
  if (typeof window === 'undefined' || typeof Audio === 'undefined') return null;
  if (audio) return audio;

  audio = new Audio(SOUND_URL);
  audio.preload = 'auto';
  audio.volume = 0.5;
  return audio;
}

/**
 * Play the notification sound.
 * No-op if the browser hasn't received a user interaction yet
 * (autoplay policy) or if Audio is unavailable.
 */
export function playNotificationSound(): void {
  if (!hasUserInteracted) return;

  const el = ensureAudioLoaded();
  if (!el) return;

  // Reset to start so rapid successive calls each produce sound
  el.currentTime = 0;
  el.play().catch(() => {
    // Silently ignore — autoplay may still be blocked in edge cases
  });
}

// Start tracking on module load
trackUserInteraction();
// Eagerly preload the audio file
ensureAudioLoaded();
